import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Chip,
  Button,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTestMode } from "context/TestModeContext";
import { adminTheme, statusColors, getStatusLabel } from "theme/adminTheme";
import {
  mockProcesses,
  mockProcessExecutions,
  mockDashboardStats,
} from "data/mockData";
import { ProcessResponse, ProcessExecutionResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { ProcessController } from "api/main/ProcessController";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import { ProcessPublicController } from "api/main/ProcessPublicController";
import { ProcessExecutionPublicController } from "api/main/ProcessExecutionPublicController";
import { useAppSelector } from "redux/hooks";
import { hasRole } from "util/UtilityFunctions";
import { RoleNames } from "models/auth/RoleNames";

// Icons
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import GroupIcon from "@mui/icons-material/Group";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: { value: string; direction: "up" | "down" };
  onClick?: () => void;
  color: string;
  gradient?: string;
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  onClick,
  color,
  gradient,
}: StatCardProps) {
  return (
    <Card
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: adminTheme.transitions.normal,
        borderRadius: adminTheme.borderRadius.lg,
        border: `1px solid ${adminTheme.colors.border}`,
        boxShadow: "none",
        overflow: "hidden",
        "&:hover": onClick
          ? {
              transform: "translateY(-4px)",
              boxShadow: adminTheme.shadows.cardHover,
              borderColor: "transparent",
            }
          : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: adminTheme.colors.textSecondary, fontWeight: 500 }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: adminTheme.borderRadius.md,
              background: gradient || `${color}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: gradient ? "white" : color,
            }}
          >
            {icon}
          </Box>
        </Box>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: adminTheme.colors.textPrimary,
          }}
        >
          {value}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted }}
            >
              {subtitle}
            </Typography>
          )}

          {trend && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color:
                  trend.direction === "up"
                    ? adminTheme.colors.success
                    : adminTheme.colors.error,
              }}
            >
              {trend.direction === "up" ? (
                <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {trend.value}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

interface QuickActionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  color: string;
  size?: "small" | "large";
}

function QuickActionCard({
  title,
  description,
  icon,
  onClick,
  color,
  size = "small",
}: QuickActionProps) {
  return (
    <Card
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: adminTheme.transitions.normal,
        borderRadius: adminTheme.borderRadius.lg,
        border: `1px solid ${adminTheme.colors.border}`,
        boxShadow: "none",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: adminTheme.shadows.cardHover,
          borderColor: color,
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ p: size === "large" ? 4 : 3 }}>
        <Box
          sx={{
            width: size === "large" ? 64 : 48,
            height: size === "large" ? 64 : 48,
            borderRadius: adminTheme.borderRadius.md,
            background: `${color}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: color,
            mb: 2,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant={size === "large" ? "h5" : "h6"}
          sx={{ fontWeight: 600, mb: 1, color: adminTheme.colors.textPrimary }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: adminTheme.colors.textSecondary, mb: 2 }}
        >
          {description}
        </Typography>
        <Button
          variant={size === "large" ? "contained" : "outlined"}
          endIcon={<ArrowForwardIcon />}
          fullWidth
          sx={
            size === "large"
              ? {
                  background: adminTheme.gradients.primary,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                }
              : {
                  borderColor: adminTheme.colors.border,
                  color: adminTheme.colors.textSecondary,
                  "&:hover": {
                    borderColor: color,
                    bgcolor: `${color}08`,
                    color: color,
                  },
                }
          }
        >
          {size === "large" ? "Pradėti" : "Atidaryti"}
        </Button>
      </Box>
    </Card>
  );
}

function getStatusIcon(status: string) {
  switch (status) {
    case "IN_PROGRESS":
      return <ScheduleIcon sx={{ fontSize: 18 }} />;
    case "FINISHED":
    case "DEAL_DONE":
      return <CheckCircleIcon sx={{ fontSize: 18 }} />;
    case "CANCELLED":
      return <ErrorIcon sx={{ fontSize: 18 }} />;
    default:
      return <WarningIcon sx={{ fontSize: 18 }} />;
  }
}

function getStatusColor(status: string) {
  const key = status.toLowerCase().replace("_", "") as keyof typeof statusColors;
  return statusColors[key] || statusColors.pending;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isTestMode } = useTestMode();
  const me = useAppSelector((state) => state.me);
  const isAdmin = hasRole(RoleNames.ADMIN, me?.info?.roles);

  // Admin APIs
  const processApi = useRef(createApi("process") as ProcessController);
  const executionApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);

  // Public APIs (for regular users)
  const processPublicApi = useRef(createApi("processpublic") as ProcessPublicController);
  const executionPublicApi = useRef(createApi("processexecution") as ProcessExecutionPublicController);

  const [processes, setProcesses] = useState<ProcessResponse[]>([]);
  const [availableProcesses, setAvailableProcesses] = useState<ProcessResponse[]>([]);
  const [executions, setExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingProcessId, setStartingProcessId] = useState<number | null>(null);

  useEffect(() => {
    if (isTestMode) {
      setProcesses(mockProcesses);
      setAvailableProcesses(mockProcesses.filter(p => p.active));
      setExecutions(mockProcessExecutions);
      setLoading(false);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestMode, isAdmin]);

  async function fetchData() {
    setLoading(true);
    try {
      if (isAdmin) {
        const [processData, executionData] = await Promise.all([
          processApi.current.findAll(),
          executionApi.current.findAll(),
        ]);
        setProcesses(processData || []);
        setExecutions(executionData || []);
      } else {
        // Regular user - use public APIs
        const [availableData, userExecutions] = await Promise.all([
          processPublicApi.current.findAll(),
          executionPublicApi.current.findAllByUser(),
        ]);
        setAvailableProcesses(availableData || []);
        setExecutions(userExecutions || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleStartProcess(processId: number) {
    setStartingProcessId(processId);
    try {
      const result = await executionPublicApi.current.create({ processDefinitionId: processId });
      if (result?.id) {
        // Navigate to the first step of the process
        navigate(`/process/execution/${result.id}/0`);
      }
    } catch (error) {
      console.error("Error starting process:", error);
    } finally {
      setStartingProcessId(null);
    }
  }

  const stats = isTestMode
    ? mockDashboardStats
    : {
        activeProcesses: processes.filter((p) => p.active).length,
        executionsToday: executions.length,
        pendingActions: executions.filter((e) => e.status === "IN_PROGRESS").length,
        activeUsers: 0,
      };

  const recentExecutions = executions.slice(0, 5);

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100%", pb: 4 }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: adminTheme.colors.textPrimary,
            }}
          >
            Sveiki sugrįžę!
          </Typography>
          <Typography
            variant="body"
            sx={{ color: adminTheme.colors.textSecondary }}
          >
            {isAdmin
              ? "Čia yra jūsų procesų automatizavimo valdymo skydelis"
              : "Čia galite pradėti naujus procesus ir sekti savo vykdymus"}
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Quick Stats - Admin */}
        {isAdmin && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Aktyvūs procesai"
                value={stats.activeProcesses}
                subtitle="Publikuoti modeliai"
                icon={<AccountTreeIcon sx={{ fontSize: 24 }} />}
                onClick={() => navigate("/process")}
                color={adminTheme.colors.primary}
                gradient={adminTheme.gradients.primary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Vykdymai"
                value={stats.executionsToday}
                subtitle="Viso vykdymų"
                icon={<PlayArrowIcon sx={{ fontSize: 24 }} />}
                onClick={() => navigate("/process/execution")}
                color={adminTheme.colors.success}
                gradient={adminTheme.gradients.success}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Laukiantys"
                value={stats.pendingActions}
                subtitle="Reikia veiksmo"
                icon={<PendingActionsIcon sx={{ fontSize: 24 }} />}
                onClick={() => navigate("/process/execution")}
                color={adminTheme.colors.warning}
                gradient={adminTheme.gradients.warning}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Patvirtintos paskyros"
                value={isTestMode ? mockDashboardStats.activeUsers : stats.activeUsers}
                subtitle="Aktyvūs vartotojai"
                icon={<GroupIcon sx={{ fontSize: 24 }} />}
                onClick={() => navigate("/account")}
                color="#9c27b0"
              />
            </Grid>
          </Grid>
        )}

        {/* Quick Stats - User */}
        {!isAdmin && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Galimi procesai"
                value={availableProcesses.length}
                subtitle="Kuriuos galite pradėti"
                icon={<AccountTreeIcon sx={{ fontSize: 24 }} />}
                color={adminTheme.colors.primary}
                gradient={adminTheme.gradients.primary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Mano vykdymai"
                value={executions.length}
                subtitle="Pradėti procesai"
                icon={<PlayArrowIcon sx={{ fontSize: 24 }} />}
                color={adminTheme.colors.success}
                gradient={adminTheme.gradients.success}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Laukia veiksmo"
                value={executions.filter((e) => e.status === "IN_PROGRESS").length}
                subtitle="Reikia jūsų veiksmo"
                icon={<PendingActionsIcon sx={{ fontSize: 24 }} />}
                color={adminTheme.colors.warning}
                gradient={adminTheme.gradients.warning}
              />
            </Grid>
          </Grid>
        )}

        {/* Quick Actions - Admin only */}
        {isAdmin && (
          <Box sx={{ mb: 4 }}>
            <Card
              onClick={() => navigate("/process/new")}
              sx={{
                cursor: "pointer",
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
                overflow: "hidden",
                transition: adminTheme.transitions.normal,
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: adminTheme.shadows.cardHover,
                  borderColor: adminTheme.colors.primary,
                },
              }}
            >
              <Box
                sx={{
                  p: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: adminTheme.borderRadius.lg,
                    background: adminTheme.gradients.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <AddIcon sx={{ fontSize: 36 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: adminTheme.colors.textPrimary }}>
                    Sukurti naują procesą
                  </Typography>
                  <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                    Kurkite procesą rankiniu būdu arba naudokite AI asistentą
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    bgcolor: "rgba(139, 92, 246, 0.1)",
                    borderRadius: adminTheme.borderRadius.md,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 18, color: "#8B5CF6" }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#8B5CF6" }}>
                    AI palaikoma
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        )}

        {/* Available Processes - User view */}
        {!isAdmin && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Galimi procesai
            </Typography>
            {availableProcesses.length === 0 ? (
              <Card
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: adminTheme.borderRadius.lg,
                  border: `1px solid ${adminTheme.colors.border}`,
                  boxShadow: "none",
                }}
              >
                <AccountTreeIcon
                  sx={{ fontSize: 48, color: adminTheme.colors.textMuted, mb: 2, opacity: 0.5 }}
                />
                <Typography sx={{ color: adminTheme.colors.textMuted }}>
                  Šiuo metu nėra galimų procesų
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {availableProcesses.map((process) => (
                  <Grid item xs={12} sm={6} md={4} key={process.id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: adminTheme.borderRadius.lg,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        transition: adminTheme.transitions.normal,
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: adminTheme.shadows.cardHover,
                          borderColor: adminTheme.colors.primary,
                        },
                      }}
                    >
                      <Box sx={{ p: 3 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: adminTheme.borderRadius.md,
                            background: adminTheme.gradients.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <AccountTreeIcon sx={{ fontSize: 28, color: "white" }} />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: adminTheme.colors.textPrimary,
                          }}
                        >
                          {process.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: adminTheme.colors.textSecondary,
                            mb: 2,
                            minHeight: 40,
                          }}
                        >
                          {process.steps?.length || 0} žingsniai
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={
                            startingProcessId === process.id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                          disabled={startingProcessId !== null}
                          onClick={() => process.id && handleStartProcess(process.id)}
                          sx={{
                            background: adminTheme.gradients.primary,
                            "&:hover": { background: adminTheme.gradients.primaryHover },
                          }}
                        >
                          {startingProcessId === process.id ? "Pradedama..." : "Pradėti"}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Recent Executions & Dashboard Info */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${adminTheme.colors.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}
                >
                  {isAdmin ? "Paskutiniai vykdymai" : "Mano vykdymai"}
                </Typography>
                {isAdmin && (
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/process/execution")}
                    sx={{ color: adminTheme.colors.primary }}
                  >
                    Visi vykdymai
                  </Button>
                )}
              </Box>
              <Box sx={{ p: 2 }}>
                {recentExecutions.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      Nėra vykdymų
                    </Typography>
                  </Box>
                ) : (
                  recentExecutions.map((execution, index) => (
                    <Box
                      key={execution.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderRadius: adminTheme.borderRadius.sm,
                        cursor: "pointer",
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                        borderBottom:
                          index < recentExecutions.length - 1
                            ? `1px solid ${adminTheme.colors.borderLight}`
                            : "none",
                      }}
                      onClick={() => {
                        if (isAdmin) {
                          navigate(`/process/execution/${execution.id}`);
                        } else {
                          // Find the current active step index for the user
                          const activeStepIndex = execution.stepExecutions?.findIndex(
                            (step) => step.status === "ACTIVE"
                          );
                          const stepIndex = activeStepIndex !== undefined && activeStepIndex >= 0 ? activeStepIndex : 0;
                          navigate(`/process/execution/${execution.id}/${stepIndex}`);
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: `${getStatusColor(execution.status || "").bg}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: getStatusColor(execution.status || "").text,
                          }}
                        >
                          {getStatusIcon(execution.status || "")}
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}
                          >
                            {execution.process?.name || "Procesas"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: adminTheme.colors.textMuted }}
                          >
                            {execution.accountInfoResponse?.email || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={getStatusLabel(execution.status)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(execution.status || "").bg,
                          color: getStatusColor(execution.status || "").text,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}
